import React, { useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Modal, Form, Button, Badge, Row, Col } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Accordion from "react-bootstrap/Accordion";
import { config } from "../../config";
import { AppContext } from "../../AppContext/Context";
import NestedObjectModal from "../modals/NestedObjectModal";
import Loader from "../loader";
import Select from "react-select";
import useHandleUnauthorized from "../../hooks/useHandleUnauthorized/UseHandleUnauthorized";
import { useNavigate } from "react-router-dom";
import NoDataFound from "../errormessage/NoDataFound";
import SweetLayout from "../../Layout/SweetLayout";

interface User {
  user_id?: string;
  name?: string;
  email?: string;
  groups?: string[];
  roles?: string[];
  last_login_date?: string;
  created_date?: string;
  updated_date?: string;
}

interface Channel {
  forum_id?: number;
  channel?: number;
  name?: string;
  type?: string;
  company_name?: string;
  forum_type?: string;
  owner_account_id?: number;
  created_at?: string;
}

interface KnowledgeSource {
  ks_id?: number;
  embedding_source_id?: number;
  name?: string;
  description?: string;
  type?: string;
  status?: string;
  owner_account_id?: number;
  created_at?: string;
}

interface KSC {
  ksc_id: number;
  name?: string;
  description?: string;
  confidential?: boolean;
  created_at?: string;
}

interface AccountData {
  account_id: number | null;
  name: string;
  stripe_customer_id: string;
  created_at: string;
  owner_user_id: string;
  subscription_id: string | null;
  plan_id: string | null;
  end_date: string | null;
  updated_at: string;
  forum_ids?: number[];
}

interface SelectOption {
  value: any;
  label: string;
}

const AccountDetailsNew: React.FC = () => {
  const nav = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const { isLoading, getIdTokenClaims, user } = useAuth0();
  const showEditButton = user?.ept_roles?.includes("EptAdmin");
  const { setIsLoad, isLoad } = useContext(AppContext);
  const [selectedTitle, setSelectedTitle] = useState("Description");
  const [data, setData] = useState<AccountData | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedObject, setSelectedObject] = useState<any>(null);
  const [channels, setChannels] = useState<SelectOption[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<SelectOption | undefined>();
  const { id } = useParams<{ id: string }>();
  const handleUnauthorized = useHandleUnauthorized();
  const [deleteChannelModal, setDeleteChannelModal] = useState(false);
  const [deleteChannelDetails, setDeleteChannelDetails] = useState<any>({});
  const [deletingChannel, setDeletingChannel] = useState(false);

  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [usersCurrentPage, setUsersCurrentPage] = useState(1);
  const [usersTotalCount, setUsersTotalCount] = useState(0);
  const [usersSearchQuery, setUsersSearchQuery] = useState("");
  const [usersRefreshTrigger, setUsersRefreshTrigger] = useState(0);
  const usersRecordsPerPage = 10;

  // Channels state
  const [channelsData, setChannelsData] = useState<Channel[]>([]);
  const [channelsCurrentPage, setChannelsCurrentPage] = useState(1);
  const [channelsTotalCount, setChannelsTotalCount] = useState(0);
  const [channelsSearchQuery, setChannelsSearchQuery] = useState("");
  const [channelsRefreshTrigger, setChannelsRefreshTrigger] = useState(0);
  const channelsRecordsPerPage = 10;

  // KSs state
  const [ksData, setKsData] = useState<KnowledgeSource[]>([]);
  const [ksCurrentPage, setKsCurrentPage] = useState(1);
  const [ksTotalCount, setKsTotalCount] = useState(0);
  const [ksSearchQuery, setKsSearchQuery] = useState("");
  const ksRecordsPerPage = 10;

  // KSCs state
  const [kscData, setKscData] = useState<KSC[]>([]);
  const [kscCurrentPage, setKscCurrentPage] = useState(1);
  const [kscTotalCount, setKscTotalCount] = useState(0);
  const [kscSearchQuery, setKscSearchQuery] = useState("");
  const kscRecordsPerPage = 10;

  const [updatedForumIDs, setUpdatedForumIDs] = useState<number[] | undefined>();

  const navigateToUserTab = (email: string): void => {
    nav(`/users?email=${email}`);
  };

  const renderEmail = (email: string): JSX.Element => (
    <span
      onClick={() => navigateToUserTab(email)}
      style={{ color: "blue", cursor: "pointer" }}
    >
      <a
        href={`/users?email=${email}`}
        onClick={(e) => e.preventDefault()}
        style={{ textDecoration: "none" }}
      >
        <div className="">{email}</div>
      </a>
    </span>
  );

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch (error) {
      return "N/A";
    }
  };

  const fetchData = async (): Promise<void> => {
    if (!isLoading) {
      setIsLoad(true);
      const endpoint = `${config.baseURL}/admin/v1/account/${id}`;
      try {
        const getidToken = await getIdTokenClaims();
        const idToken = getidToken?.__raw;
        const response = await axios.get<AccountData>(endpoint, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });
        setData(response.data);
      } catch (error: any) {
        console.error("API request error:", error.message);
        handleUnauthorized(error);
      } finally {
        setIsLoad(false);
      }
    }
  };

  const fetchChannelData = async (): Promise<void> => {
    if (!isLoading) {
      setIsLoad(true);
      const endpoint = `${config.baseURL}/admin/v1/channel_list`;
      try {
        const getidToken = await getIdTokenClaims();
        const idToken = getidToken?.__raw;
        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });
        const allChannels = Array.isArray(response.data) ? response.data : [response.data];
        setChannels(
          allChannels.map((channel: any) => ({
            value: channel.channel || channel.forum_id,
            label: `${channel.name} (${channel.channel || channel.forum_id})`,
          }))
        );
      } catch (error: any) {
        console.error("API request error:", error.message);
        handleUnauthorized(error);
      } finally {
        setIsLoad(false);
      }
    }
  };

  const fetchUsersData = useCallback(async (): Promise<void> => {
    if (!id || isLoading) {
      setUsers([]);
      setUsersTotalCount(0);
      return;
    }

    setIsLoad(true);
    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;

      const params = new URLSearchParams({
        format: "gridjs",
        page: String(usersCurrentPage),
        resultsPerPage: String(usersRecordsPerPage),
        include_roles: "true",
        include_account_info: "false",
        account_id: String(id),
      });

      if (usersSearchQuery && usersSearchQuery.trim()) {
        params.set("query", usersSearchQuery.trim());
      }

      const endpoint = `${config.baseURL}/admin/v1/user_list?${params.toString()}`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      // Handle paginated response format: { count, next, previous, results }
      if (response.data && response.data.results) {
        setUsers(response.data.results || []);
        setUsersTotalCount(response.data.count || 0);
      } else if (Array.isArray(response.data)) {
        setUsers(response.data);
        setUsersTotalCount(response.data.length);
      } else {
        setUsers([]);
        setUsersTotalCount(0);
      }
    } catch (error: any) {
      console.error("Error fetching users:", error.message);
      handleUnauthorized(error);
      setUsers([]);
      setUsersTotalCount(0);
    } finally {
      setIsLoad(false);
    }
  }, [id, isLoading, getIdTokenClaims, usersCurrentPage, usersSearchQuery, usersRecordsPerPage, handleUnauthorized, setIsLoad]);

  const fetchChannelsData = useCallback(async (): Promise<void> => {
    if (!id || isLoading) {
      setChannelsData([]);
      setChannelsTotalCount(0);
      return;
    }

    setIsLoad(true);
    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;

      const endpoint = `${config.baseURL}/admin/v1/channel_list`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      const allChannels = Array.isArray(response.data) ? response.data : [response.data];

      // Filter by account - channels where this account is owner or has access
      const accountChannels = allChannels.filter(
        (ch: any) =>
          ch.owner_account_id === parseInt(id || "0") ||
          (data?.forum_ids && data.forum_ids.includes(ch.channel))
      );

      // Apply search filter
      const searchFiltered = channelsSearchQuery.trim()
        ? accountChannels.filter(
            (ch: any) =>
              ch.name?.toLowerCase().includes(channelsSearchQuery.toLowerCase()) ||
              String(ch.channel).includes(channelsSearchQuery)
          )
        : accountChannels;

      const startIdx = (channelsCurrentPage - 1) * channelsRecordsPerPage;
      const paginatedData = searchFiltered.slice(startIdx, startIdx + channelsRecordsPerPage);

      setChannelsData(paginatedData);
      setChannelsTotalCount(searchFiltered.length);
    } catch (error: any) {
      console.error("Error fetching channels:", error.message);
      handleUnauthorized(error);
      setChannelsData([]);
      setChannelsTotalCount(0);
    } finally {
      setIsLoad(false);
    }
  }, [id, isLoading, getIdTokenClaims, channelsCurrentPage, channelsSearchQuery, channelsRecordsPerPage, handleUnauthorized, setIsLoad, data?.forum_ids]);

  const fetchKsData = useCallback(async (): Promise<void> => {
    if (!id || isLoading) {
      setKsData([]);
      setKsTotalCount(0);
      return;
    }

    setIsLoad(true);
    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;

      const endpoint = `${config.baseURL}/admin/v1/ks`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      const allKs = Array.isArray(response.data) ? response.data : [response.data];

      // Filter by account - KSs owned by this account
      const accountKs = allKs.filter((ks: any) => ks.owner_account_id === parseInt(id || "0"));

      // Apply search filter
      const searchFiltered = ksSearchQuery.trim()
        ? accountKs.filter(
            (ks: any) =>
              ks.name?.toLowerCase().includes(ksSearchQuery.toLowerCase()) ||
              ks.description?.toLowerCase().includes(ksSearchQuery.toLowerCase()) ||
              String(ks.ks_id).includes(ksSearchQuery)
          )
        : accountKs;

      const startIdx = (ksCurrentPage - 1) * ksRecordsPerPage;
      const paginatedData = searchFiltered.slice(startIdx, startIdx + ksRecordsPerPage);

      setKsData(paginatedData);
      setKsTotalCount(searchFiltered.length);
    } catch (error: any) {
      console.error("Error fetching KS data:", error.message);
      handleUnauthorized(error);
      setKsData([]);
      setKsTotalCount(0);
    } finally {
      setIsLoad(false);
    }
  }, [id, isLoading, getIdTokenClaims, ksCurrentPage, ksSearchQuery, ksRecordsPerPage, handleUnauthorized, setIsLoad]);

  const fetchKscData = useCallback(async (): Promise<void> => {
    if (!id || isLoading) {
      setKscData([]);
      setKscTotalCount(0);
      return;
    }

    setIsLoad(true);
    try {
      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;

      const params = new URLSearchParams({
        account_id: String(id),
      });

      if (kscSearchQuery && kscSearchQuery.trim()) {
        params.set("query", kscSearchQuery.trim());
      }

      const endpoint = `${config.baseURL}/admin/v1/ksc?${params.toString()}`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      const kscList = Array.isArray(response.data) ? response.data : [];

      // Apply search filter if not handled by backend
      const searchFiltered = kscSearchQuery.trim()
        ? kscList.filter(
            (ksc: any) =>
              ksc.name?.toLowerCase().includes(kscSearchQuery.toLowerCase()) ||
              String(ksc.ksc_id).includes(kscSearchQuery)
          )
        : kscList;

      const startIdx = (kscCurrentPage - 1) * kscRecordsPerPage;
      const paginatedData = searchFiltered.slice(startIdx, startIdx + kscRecordsPerPage);

      setKscData(paginatedData);
      setKscTotalCount(searchFiltered.length);
    } catch (error: any) {
      console.error("Error fetching KSC data:", error.message);
      handleUnauthorized(error);
      setKscData([]);
      setKscTotalCount(0);
    } finally {
      setIsLoad(false);
    }
  }, [id, isLoading, getIdTokenClaims, kscCurrentPage, kscSearchQuery, kscRecordsPerPage, handleUnauthorized, setIsLoad]);

  useEffect(() => {
    if (!isLoading) {
      fetchChannelData();
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, id]);

  useEffect(() => {
    if (id) {
      fetchUsersData();
    }
  }, [id, usersCurrentPage, usersSearchQuery, usersRefreshTrigger, fetchUsersData]);

  useEffect(() => {
    if (id) {
      fetchChannelsData();
    }
  }, [id, channelsCurrentPage, channelsSearchQuery, channelsRefreshTrigger, fetchChannelsData]);

  useEffect(() => {
    if (id) {
      fetchKsData();
    }
  }, [id, ksCurrentPage, ksSearchQuery, fetchKsData]);

  useEffect(() => {
    if (id) {
      fetchKscData();
    }
  }, [id, kscCurrentPage, kscSearchQuery, fetchKscData]);

  const columns = [
    { title: "Account ID", dataIndex: "account_id", key: "account_id" },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Stripe Customer ID", dataIndex: "stripe_customer_id", key: "stripe_customer_id" },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (value: any, record: any) => formatDate(value),
    },
    { title: "Owner User ID", dataIndex: "owner_user_id", key: "owner_user_id" },
    {
      title: "Subscription ID",
      dataIndex: "subscription_id",
      key: "subscription_id",
      render: (value: any, record: any) =>
        typeof value === "object" ? JSON.stringify(value) : value !== null ? value : "N/A",
    },
    {
      title: "Plan ID",
      dataIndex: "plan_id",
      key: "plan_id",
      render: (value: any, record: any) =>
        typeof value === "object" ? JSON.stringify(value) : value !== null ? value : "N/A",
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (value: any, record: any) =>
        typeof value === "object" ? JSON.stringify(value) : value !== null ? value : "N/A",
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value: any, record: any) => formatDate(value),
    },
  ];

  const dataSource = data ? [data] : [];

  const handleViewObject = (object: any, title: string): void => {
    setSelectedObject(object);
    setSelectedTitle(title);
    setModalVisible(true);
  };

  const closeModal = (): void => {
    setModalVisible(false);
  };

  const handleOptionChange = (selectedOption: SelectOption | null): void => {
    const channelId = selectedOption?.value;
    const existingForumIds = Array.isArray(data?.forum_ids) ? data.forum_ids : [];

    if (channelId === undefined || channelId === null) {
      setSelectedChannels(undefined);
      setUpdatedForumIDs(existingForumIds);
      return;
    }

    if (!existingForumIds.includes(channelId)) {
      const updatedForumIds = [...existingForumIds, channelId];
      setUpdatedForumIDs(updatedForumIds);
    } else {
      setUpdatedForumIDs(existingForumIds);
    }

    setSelectedChannels(selectedOption);
  };

  const handleKsChange = (ksId: number): void => {
    nav(`/ks/${ksId}`);
  };

  const handleSaveChannel = async (): Promise<void> => {
    setEditMode(false);

    if (!isLoading) {
      setIsLoad(true);
      try {
        const endpoint = `${config.baseURL}/admin/v1/account/${id}`;
        const getidToken = await getIdTokenClaims();
        const idToken = getidToken?.__raw;
        const payload = { forum_ids: updatedForumIDs };

        await axios.put(endpoint, payload, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });

        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });
        const updatedData = response.data;

        setData(updatedData);

        // Refresh channels data
        setChannelsRefreshTrigger((prev) => prev + 1);
        setSelectedChannels(undefined);
      } catch (error: any) {
        console.error("Error Saving channel:", error.message);
      } finally {
        setIsLoad(false);
      }
    }
  };

  const deleteChannel = async (channelId: number): Promise<void> => {
    if (!isLoading) {
      setIsLoad(true);
      const endpoint = `${config.baseURL}/admin/v1/channel/${channelId}`;
      try {
        setDeletingChannel(true);
        const getidToken = await getIdTokenClaims();
        const idToken = getidToken?.__raw;
        const response = await axios.delete(endpoint, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });
        if (response.status === 200) {
          setDeletingChannel(false);
          fetchData();
          setDeleteChannelModal(false);
        }
      } catch (error: any) {
        console.error("API request error:", error.message);
      } finally {
        setIsLoad(false);
      }
    }
  };

  const handleDeleteChannelPopup = (channelId: number, channelName: string): void => {
    setDeleteChannelModal(true);
    setDeleteChannelDetails({ channelId, channelName });
  };

  return (
    <SweetLayout>
      <div>
        <div style={{ overflowX: "auto" }}>
          <div
            onClick={() => nav(-1)}
            className="fe-5 fw-bold"
            style={{ cursor: "pointer" }}
          >
            <span className="bi bi-chevron-left"></span> Back
          </div>
          <h1>Account</h1>
          <div className="w-100 ept-data-table">
            <div style={{ overflowX: "auto", position: "relative" }}>
              {isLoad && (
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    width: "100%",
                    zIndex: 10,
                  }}
                >
                  <Loader />
                </div>
              )}

              <div className="table-container">
                <table style={{ minHeight: "106px" }}>
                  <thead>
                    <tr>
                      {columns.map((column, index) => (
                        <th key={index}>{column.title}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dataSource.map((record, index) => (
                      <tr key={index}>
                        {columns.map((column, colIndex) => (
                          <td key={colIndex}>
                            {column.render
                              ? column.render((record as any)[column.dataIndex], record)
                              : (record as any)[column.dataIndex] || "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <Accordion className="mt-5">
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <h4>Users</h4>
                </Accordion.Header>
                <Accordion.Body>
                  <div
                    style={{
                      position: "relative",
                      paddingTop: "20px",
                      paddingBottom: "20px",
                    }}
                  >
                    <Row className="mb-3">
                      <Col xs={12} md={6}>
                        <Form.Control
                          type="text"
                          placeholder="Search users by email or name..."
                          value={usersSearchQuery}
                          onChange={(e) => {
                            setUsersSearchQuery(e.target.value);
                            setUsersCurrentPage(1);
                          }}
                          style={{ maxWidth: "500px" }}
                        />
                      </Col>
                    </Row>
                  </div>
                  {users.length > 0 ? (
                    <>
                      <div className="ept-data-table">
                        <div className="table-container">
                          <table style={{ minHeight: "106px" }}>
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Roles</th>
                                <th>Last Login</th>
                                <th>Created Date</th>
                                <th>Updated Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {users.map((user, index) => (
                                <tr key={user.user_id || user.email || index}>
                                  <td>{user.email || "N/A"}</td>
                                  <td>{renderEmail(user.email || "-")}</td>
                                  <td>
                                    {user.roles &&
                                    Array.isArray(user.roles) &&
                                    user.roles.length > 0 ? (
                                      <div className="d-flex flex-wrap gap-1">
                                        {user.roles.map((role, idx) => (
                                          <Badge
                                            key={idx}
                                            bg="primary"
                                            style={{ fontSize: "0.75rem" }}
                                          >
                                            {role}
                                          </Badge>
                                        ))}
                                      </div>
                                    ) : user.groups &&
                                      Array.isArray(user.groups) &&
                                      user.groups.length > 0 ? (
                                      <div className="d-flex flex-wrap gap-1">
                                        {user.groups.map((group, idx) => (
                                          <Badge
                                            key={idx}
                                            bg="primary"
                                            style={{ fontSize: "0.75rem" }}
                                          >
                                            {group}
                                          </Badge>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-center">-</div>
                                    )}
                                  </td>
                                  <td>{formatDate(user.last_login_date)}</td>
                                  <td>{formatDate(user.created_date)}</td>
                                  <td>{formatDate(user.updated_date)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {usersTotalCount > 0 && (
                        <div className="d-flex justify-content-center mt-3">
                          <div className="btn-group" role="group">
                            <Button
                              variant={
                                usersCurrentPage === 1 ? "outline-secondary" : "secondary"
                              }
                              disabled={usersCurrentPage === 1}
                              onClick={() => setUsersCurrentPage(usersCurrentPage - 1)}
                              size="sm"
                            >
                              Previous
                            </Button>
                            <span className="btn btn-outline-secondary disabled">
                              Page {usersCurrentPage} of{" "}
                              {Math.ceil(usersTotalCount / usersRecordsPerPage)} (
                              {usersTotalCount} total)
                            </span>
                            <Button
                              variant={
                                usersCurrentPage >=
                                Math.ceil(usersTotalCount / usersRecordsPerPage)
                                  ? "outline-secondary"
                                  : "secondary"
                              }
                              disabled={
                                usersCurrentPage >=
                                Math.ceil(usersTotalCount / usersRecordsPerPage)
                              }
                              onClick={() => setUsersCurrentPage(usersCurrentPage + 1)}
                              size="sm"
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <NoDataFound style={{ minHeight: "200px" }} />
                  )}
                </Accordion.Body>
              </Accordion.Item>
              
              <Accordion.Item className="mt-5" eventKey="1">
                <Accordion.Header>
                  <h4>Channels</h4>
                </Accordion.Header>
                <Accordion.Body>
                  <div
                    style={{
                      position: "relative",
                      paddingTop: "20px",
                      paddingBottom: "20px",
                    }}
                  >
                    {showEditButton && !editMode && (
                      <div
                        style={{
                          position: "absolute",
                          right: "0",
                          top: "20px",
                          display: "flex",
                          gap: "8px",
                        }}
                      >
                        <Button
                          variant="dark"
                          size="sm"
                          onClick={() => setEditMode(true)}
                          className="me-2"
                        >
                          Add Channel
                        </Button>
                      </div>
                    )}

                    <Row className="mb-3">
                      <Col xs={12} md={6}>
                        <Form.Control
                          type="text"
                          placeholder="Search channels by name or ID..."
                          value={channelsSearchQuery}
                          onChange={(e) => {
                            setChannelsSearchQuery(e.target.value);
                            setChannelsCurrentPage(1);
                          }}
                          style={{ maxWidth: "500px" }}
                        />
                      </Col>
                    </Row>

                    {editMode && (
                      <div className="mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <div style={{ flex: 1, maxWidth: "400px" }}>
                            <Select
                              id="channel-select"
                              value={selectedChannels}
                              onChange={handleOptionChange}
                              options={channels}
                              placeholder="Select Channel to Add"
                            />
                          </div>
                          <Button variant="success" size="sm" onClick={handleSaveChannel}>
                            <i className="fa fa-save me-1"></i>
                            Save
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setEditMode(false);
                              setSelectedChannels(undefined);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {channelsData.length > 0 ? (
                    <>
                      <div className="ept-data-table">
                        <div className="table-container">
                          <table style={{ minHeight: "106px" }}>
                            <thead>
                              <tr>
                                <th>Channel ID</th>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Company Name</th>
                                <th>Forum Type</th>
                                <th>Created At</th>
                                {showEditButton && <th className="text-center">Action</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {channelsData.map((value, index) => (
                                <tr key={value.channel || value.forum_id || index}>
                                  <td>
                                    {value.forum_id || value.channel ? (
                                      <Link to={`/channel/${value.forum_id || value.channel}`}>
                                        {value.forum_id || value.channel}
                                      </Link>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                  <td>{value.name || "-"}</td>
                                  <td>{value.type || "-"}</td>
                                  <td>{value.company_name || "-"}</td>
                                  <td>{value.forum_type || "-"}</td>
                                  <td>{formatDate(value.created_at)}</td>
                                  {showEditButton && (
                                    <td className="text-center">
                                      <i
                                        className="fa fa-trash"
                                        style={{ cursor: "pointer" }}
                                        onClick={() =>
                                          handleDeleteChannelPopup(
                                            value.forum_id || value.channel || 0,
                                            value.name || ""
                                          )
                                        }
                                      ></i>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {channelsTotalCount > 0 && (
                        <div className="d-flex justify-content-center mt-3">
                          <div className="btn-group" role="group">
                            <Button
                              variant={
                                channelsCurrentPage === 1
                                  ? "outline-secondary"
                                  : "secondary"
                              }
                              disabled={channelsCurrentPage === 1}
                              onClick={() =>
                                setChannelsCurrentPage(channelsCurrentPage - 1)
                              }
                              size="sm"
                            >
                              Previous
                            </Button>
                            <span className="btn btn-outline-secondary disabled">
                              Page {channelsCurrentPage} of{" "}
                              {Math.ceil(channelsTotalCount / channelsRecordsPerPage)} (
                              {channelsTotalCount} total)
                            </span>
                            <Button
                              variant={
                                channelsCurrentPage >=
                                Math.ceil(channelsTotalCount / channelsRecordsPerPage)
                                  ? "outline-secondary"
                                  : "secondary"
                              }
                              disabled={
                                channelsCurrentPage >=
                                Math.ceil(channelsTotalCount / channelsRecordsPerPage)
                              }
                              onClick={() =>
                                setChannelsCurrentPage(channelsCurrentPage + 1)
                              }
                              size="sm"
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <NoDataFound style={{ minHeight: "200px" }} />
                  )}
                </Accordion.Body>
              </Accordion.Item>
              
              <Accordion.Item className="mt-5" eventKey="2">
                <Accordion.Header>
                  <h4>KSs</h4>
                </Accordion.Header>
                <Accordion.Body>
                  <div
                    style={{
                      position: "relative",
                      paddingTop: "20px",
                      paddingBottom: "20px",
                    }}
                  >
                    <Row className="mb-3">
                      <Col xs={12} md={6}>
                        <Form.Control
                          type="text"
                          placeholder="Search KSs by name or ID..."
                          value={ksSearchQuery}
                          onChange={(e) => {
                            setKsSearchQuery(e.target.value);
                            setKsCurrentPage(1);
                          }}
                          style={{ maxWidth: "500px" }}
                        />
                      </Col>
                    </Row>
                  </div>

                  {ksData.length > 0 ? (
                    <>
                      <div className="ept-data-table">
                        <div className="table-container">
                          <table style={{ minHeight: "106px" }}>
                            <thead>
                              <tr>
                                <th>KS ID</th>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Created At</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ksData.map((value, index) => (
                                <tr key={value.ks_id || index}>
                                  <td
                                    onClick={() => value.ks_id && handleKsChange(value.ks_id)}
                                    style={{ color: "blue", cursor: "pointer" }}
                                  >
                                    {value.ks_id || "-"}
                                  </td>
                                  <td>{value.name || "-"}</td>
                                  <td>{value.description || "-"}</td>
                                  <td>{value.type || "-"}</td>
                                  <td>{value.status || "-"}</td>
                                  <td>{formatDate(value.created_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {ksTotalCount > 0 && (
                        <div className="d-flex justify-content-center mt-3">
                          <div className="btn-group" role="group">
                            <Button
                              variant={
                                ksCurrentPage === 1 ? "outline-secondary" : "secondary"
                              }
                              disabled={ksCurrentPage === 1}
                              onClick={() => setKsCurrentPage(ksCurrentPage - 1)}
                              size="sm"
                            >
                              Previous
                            </Button>
                            <span className="btn btn-outline-secondary disabled">
                              Page {ksCurrentPage} of{" "}
                              {Math.ceil(ksTotalCount / ksRecordsPerPage)} ({ksTotalCount}{" "}
                              total)
                            </span>
                            <Button
                              variant={
                                ksCurrentPage >= Math.ceil(ksTotalCount / ksRecordsPerPage)
                                  ? "outline-secondary"
                                  : "secondary"
                              }
                              disabled={
                                ksCurrentPage >= Math.ceil(ksTotalCount / ksRecordsPerPage)
                              }
                              onClick={() => setKsCurrentPage(ksCurrentPage + 1)}
                              size="sm"
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <NoDataFound style={{ minHeight: "200px" }} />
                  )}
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item className="mt-5" eventKey="3">
                <Accordion.Header>
                  <h4>KSCs</h4>
                </Accordion.Header>
                <Accordion.Body>
                  <div
                    style={{
                      position: "relative",
                      paddingTop: "20px",
                      paddingBottom: "20px",
                    }}
                  >
                    <Row className="mb-3">
                      <Col xs={12} md={6}>
                        <Form.Control
                          type="text"
                          placeholder="Search KSCs by name or ID..."
                          value={kscSearchQuery}
                          onChange={(e) => {
                            setKscSearchQuery(e.target.value);
                            setKscCurrentPage(1);
                          }}
                          style={{ maxWidth: "500px" }}
                        />
                      </Col>
                    </Row>
                  </div>

                  {kscData.length > 0 ? (
                    <>
                      <div className="ept-data-table">
                        <div className="table-container">
                          <table style={{ minHeight: "106px" }}>
                            <thead>
                              <tr>
                                <th>KSC ID</th>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Confidential</th>
                                <th>Created At</th>
                              </tr>
                            </thead>
                            <tbody>
                              {kscData.map((value, index) => (
                                <tr key={value.ksc_id || index}>
                                  <td
                                    onClick={() => nav(`/ksc-details/${value.ksc_id}`)}
                                    style={{ color: "blue", cursor: "pointer" }}
                                  >
                                    {value.ksc_id || "-"}
                                  </td>
                                  <td>{value.name || "-"}</td>
                                  <td>{value.description || "-"}</td>
                                  <td>
                                    {value.confidential ? (
                                      <Badge bg="warning" text="dark" style={{ fontSize: "0.75rem" }}>
                                        Yes
                                      </Badge>
                                    ) : (
                                      <Badge bg="secondary" style={{ fontSize: "0.75rem" }}>
                                        No
                                      </Badge>
                                    )}
                                  </td>
                                  <td>{formatDate(value.created_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {kscTotalCount > 0 && (
                        <div className="d-flex justify-content-center mt-3">
                          <div className="btn-group" role="group">
                            <Button
                              variant={
                                kscCurrentPage === 1 ? "outline-secondary" : "secondary"
                              }
                              disabled={kscCurrentPage === 1}
                              onClick={() => setKscCurrentPage(kscCurrentPage - 1)}
                              size="sm"
                            >
                              Previous
                            </Button>
                            <span className="btn btn-outline-secondary disabled">
                              Page {kscCurrentPage} of{" "}
                              {Math.ceil(kscTotalCount / kscRecordsPerPage)} ({kscTotalCount}{" "}
                              total)
                            </span>
                            <Button
                              variant={
                                kscCurrentPage >= Math.ceil(kscTotalCount / kscRecordsPerPage)
                                  ? "outline-secondary"
                                  : "secondary"
                              }
                              disabled={
                                kscCurrentPage >= Math.ceil(kscTotalCount / kscRecordsPerPage)
                              }
                              onClick={() => setKscCurrentPage(kscCurrentPage + 1)}
                              size="sm"
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <NoDataFound style={{ minHeight: "200px" }} />
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        </div>

        <Modal
          show={deleteChannelModal}
          onHide={() => setDeleteChannelModal(false)}
          size="lg"
          aria-labelledby="contained-modal-title-vcenter"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Delete Channel</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete the channel{" "}
            {deleteChannelDetails.channelName} - {deleteChannelDetails.channelId}?
            {deletingChannel && (
              <div className="text-center mt-3">
                Please wait...
                <br />
                We are deleting the channel.
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-secondary"
              onClick={() => setDeleteChannelModal(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              disabled={deletingChannel}
              onClick={() => deleteChannel(deleteChannelDetails.channelId)}
            >
              Delete
            </button>
          </Modal.Footer>
        </Modal>

        <NestedObjectModal
          title={selectedTitle}
          visible={modalVisible}
          data={selectedObject}
          onClose={closeModal}
        />
      </div>
    </SweetLayout>
  );
};

export default AccountDetailsNew;

